import { router, useForm } from '@inertiajs/react'
import { FormEvent, useState } from 'react'
import { format } from 'date-fns'
import { ArrowLeftIcon, UserPlusIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AppHeader } from '@/components/app-header'
import { toast } from 'sonner'

interface Site {
  id: number
  name: string
}

interface AdminPatientsNewProps {
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
    }
  }
  sites?: Site[]
  errors?: Record<string, string[]>
}

export default function AdminPatientsNew({ auth, sites = [], errors }: AdminPatientsNewProps) {
  const [dobDate, setDobDate] = useState<Date | undefined>(undefined)

  const { data, setData, post, processing } = useForm({
    site_id: '',
    legal_name: '',
    alias_name: '',
    phone_number: '',
    email: '',
    date_of_birth: '',
    zip_code: '',
    password: '',
    password_confirmation: '',
    emergency_contact_1_name: '',
    emergency_contact_1_phone: '',
    emergency_contact_2_name: '',
    emergency_contact_2_phone: '',
    emergency_contact_3_name: '',
    emergency_contact_3_phone: '',
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    post('/admin/patients', {
      preserveScroll: true,
      onSuccess: () => {
        toast.success('Patient created successfully')
      },
      onError: (errors) => {
        const errorMessages = Object.values(errors).flat()
        errorMessages.forEach((error) => toast.error(String(error)))
      },
    })
  }

  const handleBack = () => {
    router.visit('/admin/patients')
  }

  return (
    <>
      <AppHeader
        breadcrumbs={[
          { label: 'Patients', href: '/admin/patients' },
          { label: 'New Patient' },
        ]}
      />

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50/30 to-purple-50 p-6 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900">
        <div className="mx-auto max-w-3xl space-y-6">
          {/* Back Button */}
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Patients
          </Button>

          {/* Main Form Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
                  <UserPlusIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <CardTitle>Quick Patient Registration</CardTitle>
                  <CardDescription>
                    Create a new patient with minimal required information. A secure password will be generated automatically.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Required Fields Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Required Information</h3>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="site_id">Clinic Site *</Label>
                      <Select value={data.site_id.toString()} onValueChange={(value) => setData('site_id', value)}>
                        <SelectTrigger id="site_id">
                          <SelectValue placeholder="Select clinic site" />
                        </SelectTrigger>
                        <SelectContent>
                          {sites && sites.length > 0 ? (
                            sites.map((site) => (
                              <SelectItem key={site.id} value={site.id.toString()}>
                                {site.name}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="" disabled>No sites available</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      {errors?.site_id && (
                        <p className="text-sm text-red-600">{errors.site_id[0]}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="legal_name">Legal Name *</Label>
                      <Input
                        id="legal_name"
                        value={data.legal_name}
                        onChange={(e) => setData('legal_name', e.target.value)}
                        placeholder="John Doe"
                        required
                      />
                      {errors?.legal_name && (
                        <p className="text-sm text-red-600">{errors.legal_name[0]}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="phone_number">Phone Number</Label>
                      <Input
                        id="phone_number"
                        type="tel"
                        value={data.phone_number}
                        onChange={(e) => setData('phone_number', e.target.value)}
                        placeholder="555-123-4567"
                      />
                      {errors?.phone_number && (
                        <p className="text-sm text-red-600">{errors.phone_number[0]}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Date of Birth *</Label>
                      <DatePicker
                        value={dobDate}
                        onChange={(date) => {
                          setDobDate(date)
                          setData('date_of_birth', date ? format(date, 'yyyy-MM-dd') : '')
                        }}
                        placeholder="Select date of birth"
                      />
                      {errors?.date_of_birth && (
                        <p className="text-sm text-red-600">{errors.date_of_birth[0]}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email (for portal access)</Label>
                      <Input
                        id="email"
                        type="email"
                        value={data.email}
                        onChange={(e) => setData('email', e.target.value)}
                        placeholder="patient@example.com"
                      />
                      {errors?.email && (
                        <p className="text-sm text-red-600">{errors.email[0]}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="zip_code">ZIP Code *</Label>
                      <Input
                        id="zip_code"
                        value={data.zip_code}
                        onChange={(e) => setData('zip_code', e.target.value)}
                        placeholder="12345"
                        required
                      />
                      {errors?.zip_code && (
                        <p className="text-sm text-red-600">{errors.zip_code[0]}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="password">Password (for portal access)</Label>
                      <Input
                        id="password"
                        type="password"
                        value={data.password}
                        onChange={(e) => setData('password', e.target.value)}
                        placeholder="Leave blank to auto-generate"
                      />
                      {errors?.password && (
                        <p className="text-sm text-red-600">{errors.password[0]}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password_confirmation">Confirm Password</Label>
                      <Input
                        id="password_confirmation"
                        type="password"
                        value={data.password_confirmation}
                        onChange={(e) => setData('password_confirmation', e.target.value)}
                        placeholder="Confirm password"
                      />
                      {errors?.password_confirmation && (
                        <p className="text-sm text-red-600">{errors.password_confirmation[0]}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Optional Fields Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Optional Information</h3>

                  <div className="space-y-2">
                    <Label htmlFor="alias_name">Alias Name (for privacy)</Label>
                    <Input
                      id="alias_name"
                      value={data.alias_name}
                      onChange={(e) => setData('alias_name', e.target.value)}
                      placeholder="Preferred name"
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="emergency_contact_1_name">Emergency Contact 1 Name</Label>
                      <Input
                        id="emergency_contact_1_name"
                        value={data.emergency_contact_1_name}
                        onChange={(e) => setData('emergency_contact_1_name', e.target.value)}
                        placeholder="Jane Doe"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="emergency_contact_1_phone">Emergency Contact 1 Phone</Label>
                      <Input
                        id="emergency_contact_1_phone"
                        type="tel"
                        value={data.emergency_contact_1_phone}
                        onChange={(e) => setData('emergency_contact_1_phone', e.target.value)}
                        placeholder="555-987-6543"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="emergency_contact_2_name">Emergency Contact 2 Name</Label>
                      <Input
                        id="emergency_contact_2_name"
                        value={data.emergency_contact_2_name}
                        onChange={(e) => setData('emergency_contact_2_name', e.target.value)}
                        placeholder="John Smith"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="emergency_contact_2_phone">Emergency Contact 2 Phone</Label>
                      <Input
                        id="emergency_contact_2_phone"
                        type="tel"
                        value={data.emergency_contact_2_phone}
                        onChange={(e) => setData('emergency_contact_2_phone', e.target.value)}
                        placeholder="555-111-2222"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="emergency_contact_3_name">Emergency Contact 3 Name</Label>
                      <Input
                        id="emergency_contact_3_name"
                        value={data.emergency_contact_3_name}
                        onChange={(e) => setData('emergency_contact_3_name', e.target.value)}
                        placeholder="Sarah Johnson"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="emergency_contact_3_phone">Emergency Contact 3 Phone</Label>
                      <Input
                        id="emergency_contact_3_phone"
                        type="tel"
                        value={data.emergency_contact_3_phone}
                        onChange={(e) => setData('emergency_contact_3_phone', e.target.value)}
                        placeholder="555-333-4444"
                      />
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-4">
                  <Button type="button" variant="outline" onClick={handleBack}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={processing}>
                    {processing ? 'Creating...' : 'Create Patient'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20">
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <strong>Note:</strong> You can set a password for the patient, or leave it blank to auto-generate a secure password.
                The HCV risk questionnaire and full consents can be completed later by staff or by the patient through the portal.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}
